/**
 * 'v-drawable-canvas' directive
 * Allow the user to draw on a specific JS DOM elt with mouse or finger
 */

// Reference ; Stroke will be `this.lineWidth` px on a `SCREEN_SIZE_REF` px height/width image
const SCREEN_SIZE_REF = 1280;

function getRelativeLineWidth() {
  return Math.max(this.el.width, this.el.height) * this.lineWidth / SCREEN_SIZE_REF;
}

function getPointerPosition(e) {
  const el = e.target;
  const elRect = el.getBoundingClientRect();
  let x;
  let y;

  // Get pointer position relative to document
  if (window.TouchEvent && e instanceof TouchEvent) {
    x = e.touches[0].pageX;
    y = e.touches[0].pageY;
  } else {
    x = e.pageX;
    y = e.pageY;
  }
  // Get pointer position relative to canvas element,
  // then convert pointer DOM position to canvas coordinates position
  x = (x - elRect.left) * el.width / elRect.width;
  y = (y - elRect.top) * el.height / elRect.height;
  return [x, y];
}

function draw(x, y, isDown) {
  if (!isDown) {
    this.ctx.beginPath();
    this.ctx.strokeStyle = this.lineColor;
    this.ctx.lineWidth = Math.max(1, getRelativeLineWidth.call(this));
    this.ctx.lineJoin = 'round';
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(x, y);
    this.ctx.closePath();
    this.ctx.stroke();
  }
  this.lastX = x;
  this.lastY = y;
}

function drawCircle(x, y, r) {
  r = Math.max(0, r);
  this.ctx.beginPath();
  this.ctx.fillStyle = this.lineColor;
  if (r >= 0.5) {
    this.ctx.arc(x, y, r, 0, 2 * Math.PI);
  } else {
    this.ctx.strokeRect(x, y, 1, 1);
  }
  this.ctx.fill();
}

function onPointerDown(e) {
  if (e instanceof PointerEvent) {
    if (!this.simultaneousEvents) {
      this.simultaneousEvents = {};
    }
    this.simultaneousEvents[`${e.pointerId}`] = e;
  }
  // When using pinter events, prevent multiple finger touches (if not uing pointer events, browser is too old anyway)
  if (!this.simultaneousEvents || Object.values(this.simultaneousEvents).length === 1) {
    this.isDrawing = true;
    const pointerPos = getPointerPosition.call(this, e);
    draw.call(this, pointerPos[0], pointerPos[1], true);
    this.callback();
  } else {
    this.isDrawing = false;
  }
}

function onPointerMove(e) {
  if (this.isDrawing) {
    const pointerPos = getPointerPosition.call(this, e);
    draw.call(this, pointerPos[0], pointerPos[1], false);
  }
}

function onPointerUp(e) {
  // Remove the pointer events from active pointer events to re-enable single pointer drawing
  if (e instanceof PointerEvent) {
    if (this.simultaneousEvents && this.simultaneousEvents[`${e.pointerId}`]) {
      delete this.simultaneousEvents[`${e.pointerId}`];
    }
  }

  if (this.isDrawing) {
    const pointerPos = getPointerPosition.call(this, e);
    drawCircle.call(this, pointerPos[0], pointerPos[1], getRelativeLineWidth.call(this) / 2);
  }
  this.isDrawing = false;
}

export default {
  bind(el, binding) {
    if (!(el instanceof HTMLCanvasElement)) {
      console.error('Vue directive: drawable-canvas: Must be a canvas element');
      return;
    }
    const data = {
      el,
      callback: binding.value instanceof Function ? binding.value : () => {},
      ctx: el.getContext('2d'),
      lineColor: 'red',
      lineWidth: 7,
      lastX: null,
      lastY: null,
      isDrawing: false,
    };
    // Register events
    if (window.PointerEvent) {
      el.addEventListener('pointerdown', onPointerDown.bind(data), false);
      el.addEventListener('pointerup', onPointerUp.bind(data), false);
      el.addEventListener('pointermove', onPointerMove.bind(data), false);
      el.addEventListener('pointerout', onPointerUp.bind(data), false);
    } else {
      el.addEventListener('mousedown', onPointerDown.bind(data), false);
      el.addEventListener('mouseup', onPointerUp.bind(data), false);
      el.addEventListener('mousemove', onPointerMove.bind(data), false);
      el.addEventListener('mouseout', onPointerUp.bind(data), false);
      el.addEventListener('mouseleave', onPointerUp.bind(data), false);
      if (window.TouchEvent) {
        el.addEventListener('touchstart', onPointerDown.bind(data), false);
        el.addEventListener('touchmove', (e) => {
          // Prevent scrolling, messing with getPointerPosition calculation
          // Even with nothing to scroll, there is a 'virtual' scroll happening
          e.preventDefault();
          onPointerMove.call(data, e);
        }, false);
      }
    }
  },
};
